import React, { useState, useEffect, useRef } from 'react';
import API_BASE_URL from '../../../../../js/urlHelper';
import { fetchWithAuth } from '../../../../../js/authToken';

const ActividadesForm = ({ actividades = {}, errors = {}, onChange, isEditing = false }) => {
  const [searchText, setSearchText] = useState({ ciiu: '', noSensibles: '' });
  const [showDropdown, setShowDropdown] = useState({ ciiu: false, noSensibles: false });
  const [searchResults, setSearchResults] = useState({ ciiu: [], noSensibles: [] });
  const [selectedActivities, setSelectedActivities] = useState({
    ciiu: null,
    noSensibles: null,
  });
  const [loading, setLoading] = useState({ ciiu: false, noSensibles: false });
  const [isInputDisabled, setIsInputDisabled] = useState({
    ciiu: false,
    noSensibles: false,
  });

  const isInitialLoad = useRef(true);
  const ciiuRef = useRef(null);
  const noSensiblesRef = useRef(null);
  const searchTimeouts = useRef({ ciiu: null, noSensibles: null });

  // Inicialización segura y condicional
  useEffect(() => {
    const initializeActivities = async () => {
      let newCiiu = selectedActivities.ciiu;
      let newNoSensibles = selectedActivities.noSensibles;
      let ciiuDisabled = isInputDisabled.ciiu;
      let noSensiblesDisabled = isInputDisabled.noSensibles;

      // Handle ciiu activity
      if (actividades.ciiu && isInitialLoad.current) {
        if (
          typeof actividades.ciiu === 'object' &&
          actividades.ciiu.codigo &&
          actividades.ciiu.descripcion
        ) {
          newCiiu = actividades.ciiu;
          ciiuDisabled = isEditing;
        } else if (isEditing) {
          try {
            const response = await fetchWithAuth(
              `${API_BASE_URL}/api/asesor/actividades/ciiu?id=${actividades.ciiu}`
            );
            const data = await response.json();
            newCiiu = data.activity_ciiu?.data[0] || null;
            ciiuDisabled = isEditing && !!newCiiu;
          } catch (error) {
            console.error('Error al cargar actividad CIIU:', error);
          }
        }
      }

      // Handle non-sensitive activity
      if (actividades.noSensibles && isInitialLoad.current) {
        if (
          typeof actividades.noSensibles === 'object' &&
          actividades.noSensibles.sector &&
          actividades.noSensibles.actividad
        ) {
          newNoSensibles = actividades.noSensibles;
          noSensiblesDisabled = isEditing;
        } else if (isEditing) {
          try {
            const response = await fetchWithAuth(
              `${API_BASE_URL}/api/asesor/actividades/no-sensibles?id=${actividades.noSensibles}`
            );
            const data = await response.json();
            newNoSensibles = data.activity_no_sensible?.data[0] || null;
            noSensiblesDisabled = isEditing && !!newNoSensibles;
          } catch (error) {
            console.error('Error al cargar actividad no sensible:', error);
          }
        }
      }

      // Solo actualizar el estado si hay cambios
      if (
        newCiiu !== selectedActivities.ciiu ||
        newNoSensibles !== selectedActivities.noSensibles
      ) {
        setSelectedActivities({
          ciiu: newCiiu,
          noSensibles: newNoSensibles,
        });
        // Solo llamar a onChange si los valores han cambiado
        if (newCiiu !== selectedActivities.ciiu) {
          console.log('Actualizando ciiu en onChange:', newCiiu);
          onChange('actividadesEconomicas.ciiu', newCiiu);
        }
        if (newNoSensibles !== selectedActivities.noSensibles) {
          console.log('Actualizando noSensibles en onChange:', newNoSensibles);
          onChange('actividadesEconomicas.noSensibles', newNoSensibles);
        }
      }

      if (
        ciiuDisabled !== isInputDisabled.ciiu ||
        noSensiblesDisabled !== isInputDisabled.noSensibles
      ) {
        setIsInputDisabled({
          ciiu: ciiuDisabled,
          noSensibles: noSensiblesDisabled,
        });
      }
    };

    initializeActivities();
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
    }
  }, [actividades, isEditing, onChange, selectedActivities, isInputDisabled]);

  const searchActivities = async (field, searchValue) => {
    if (!searchValue.trim()) {
      setSearchResults((prev) => ({ ...prev, [field]: [] }));
      return;
    }

    setLoading((prev) => ({ ...prev, [field]: true }));
    try {
      const endpoint =
        field === 'ciiu'
          ? `${API_BASE_URL}/api/admin/calculadora/actividades/ciiu`
          : `${API_BASE_URL}/api/admin/calculadora/actividades/no-sensibles`;

      const response = await fetchWithAuth(`${endpoint}?search=${encodeURIComponent(searchValue)}`);
      const data = await response.json();

      const items =
        field === 'ciiu'
          ? data.activity_ciiu?.data || []
          : data.activity_no_sensible?.data || [];

      setSearchResults((prev) => ({ ...prev, [field]: items }));
    } catch (error) {
      console.error(`Error al buscar ${field}:`, error);
      setSearchResults((prev) => ({ ...prev, [field]: [] }));
    } finally {
      setLoading((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleSearchChange = (field, value) => {
    if (isInputDisabled[field]) return;
    setSearchText((prev) => ({ ...prev, [field]: value }));
    setShowDropdown((prev) => ({ ...prev, [field]: true }));

    clearTimeout(searchTimeouts.current[field]);
    searchTimeouts.current[field] = setTimeout(() => searchActivities(field, value), 300);
  };

  const handleSelectItem = (field, item) => {
    if (isInputDisabled[field] || !item) return;
    setSelectedActivities((prev) => ({ ...prev, [field]: item }));
    setSearchText((prev) => ({ ...prev, [field]: '' }));
    setShowDropdown((prev) => ({ ...prev, [field]: false }));
    setIsInputDisabled((prev) => ({ ...prev, [field]: isEditing }));

    // Depuración: Confirmar que se llama onChange
    console.log(`Seleccionando ${field}:`, item);
    onChange(`actividadesEconomicas.${field}`, item);
  };

  const handleRemoveItem = (field) => {
    setSelectedActivities((prev) => ({ ...prev, [field]: null }));
    setSearchText((prev) => ({ ...prev, [field]: '' }));
    setIsInputDisabled((prev) => ({ ...prev, [field]: false }));
    onChange(`actividadesEconomicas.${field}`, null);
  };

  const inputClass = (field) =>
    `w-full rounded-xl border px-3 py-2.5 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 ${
      errors[`actividadesEconomicas.${field}`] ? 'border-red-500 ring-2 ring-red-100' : 'border-slate-300'
    } ${isInputDisabled[field] ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : 'bg-white'}`;

  // Manejo de cierre de dropdown SOLO cuando se da click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        ciiuRef.current &&
        !ciiuRef.current.contains(event.target) &&
        noSensiblesRef.current &&
        !noSensiblesRef.current.contains(event.target)
      ) {
        setShowDropdown({ ciiu: false, noSensibles: false });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderSelectedActivity = (activity, field) => {
    if (!activity) return null;
    return (
      <div className="mt-2 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-3">
        <div>
          {field === 'ciiu' ? (
            <p className="font-medium">
              {activity.codigo} - {activity.descripcion}
            </p>
          ) : (
            <>
              <p className="font-medium">{activity.sector}</p>
              <p className="text-sm text-slate-600">{activity.actividad}</p>
            </>
          )}
        </div>
        <button
          onClick={() => handleRemoveItem(field)}
          className="text-red-500 transition hover:text-red-700 text-lg"
          aria-label={`ELIMINAR ACTIVIDAD SELECCIONADA DE ${field === 'ciiu' ? 'CIIU' : 'NO SENSIBLE'}`}
        >
          ×
        </button>
      </div>
    );
  };

  return (
    <div className="ui-card p-5 md:p-6">
      <div className="mb-4 flex items-center gap-3 border-b border-slate-200 pb-3">
        <div className="h-8 w-1.5 rounded-full bg-red-600" />
        <h3 className="text-base font-semibold uppercase tracking-[0.08em] text-slate-800">ACTIVIDADES DEL CLIENTE</h3>
      </div>

      <p className="mb-4 text-sm text-slate-600">
        SELECCIONE 1 ACTIVIDAD CIIU Y 1 ACTIVIDAD NO SENSIBLE
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Actividad CIIU */}
        <div className="relative" ref={ciiuRef}>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
            ACTIVIDAD CIIU (1)
          </label>
          <input
            value={
              isInputDisabled.ciiu && selectedActivities.ciiu
                ? `${selectedActivities.ciiu.codigo} - ${selectedActivities.ciiu.descripcion}`
                : searchText.ciiu
            }
            onChange={(e) => handleSearchChange('ciiu', e.target.value)}
            onClick={(e) => {
              if (isInputDisabled.ciiu) return;
              e.stopPropagation();
              setShowDropdown((prev) => ({ ...prev, ciiu: true }));
            }}
            placeholder={
              isInputDisabled.ciiu
                ? ''
                : selectedActivities.ciiu
                ? 'BUSCAR OTRO CÓDIGO CIIU...'
                : 'SELECCIONE CÓDIGO CIIU (OBLIGATORIO)'
            }
            className={inputClass('ciiu')}
            disabled={isInputDisabled.ciiu}
            aria-label="BUSCAR ACTIVIDAD CIIU"
          />
          {errors['actividadesEconomicas.ciiu'] && (
            <span className="mt-1 block text-xs font-medium text-red-600">
              {errors['actividadesEconomicas.ciiu']}
            </span>
          )}

          {!isInputDisabled.ciiu && showDropdown.ciiu && (
            <div
              className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              {loading.ciiu ? (
                <div className="p-2 text-center text-slate-500">BUSCANDO...</div>
              ) : searchResults.ciiu.length > 0 ? (
                searchResults.ciiu.map((item) => (
                  <div
                    key={item.idCiiu}
                    className="cursor-pointer border-b border-slate-100 p-2 hover:bg-slate-50"
                    onClick={() => handleSelectItem('ciiu', item)}
                  >
                    <p className="font-medium">{item.codigo}</p>
                    <p className="text-sm text-slate-600">{item.descripcion}</p>
                    {item.riesgo && (
                      <p className="text-xs text-slate-500">RIESGO: {item.riesgo}</p>
                    )}
                  </div>
                ))
              ) : searchText.ciiu ? (
                <div className="p-2 text-center text-slate-500">NO SE ENCONTRARON RESULTADOS</div>
              ) : (
                <div className="p-2 text-center text-slate-500">ESCRIBA PARA BUSCAR</div>
              )}
            </div>
          )}

          {selectedActivities.ciiu && renderSelectedActivity(selectedActivities.ciiu, 'ciiu')}
        </div>
        {/* Actividad no sensible */}
        <div className="relative" ref={noSensiblesRef}>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
            ACTIVIDAD NO SENSIBLE (1)
          </label>
          <input
            value={
              isInputDisabled.noSensibles && selectedActivities.noSensibles
                ? selectedActivities.noSensibles.actividad
                : searchText.noSensibles
            }
            onChange={(e) => handleSearchChange('noSensibles', e.target.value)}
            onClick={(e) => {
              if (isInputDisabled.noSensibles) return;
              e.stopPropagation();
              setShowDropdown((prev) => ({ ...prev, noSensibles: true }));
            }}
            placeholder={
              isInputDisabled.noSensibles
                ? ''
                : selectedActivities.noSensibles
                ? 'BUSCAR OTRA ACTIVIDAD...'
                : 'SELECCIONE ACTIVIDAD NO SENSIBLE'
            }
            className={inputClass('noSensibles')}
            disabled={isInputDisabled.noSensibles}
            aria-label="BUSCAR ACTIVIDAD NO SENSIBLE"
          />
          {errors['actividadesEconomicas.noSensibles'] && (
            <span className="mt-1 block text-xs font-medium text-red-600">
              {errors['actividadesEconomicas.noSensibles']}
            </span>
          )}

          {!isInputDisabled.noSensibles && showDropdown.noSensibles && (
            <div
              className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              {loading.noSensibles ? (
                <div className="p-2 text-center text-slate-500">BUSCANDO...</div>
              ) : searchResults.noSensibles.length > 0 ? (
                searchResults.noSensibles.map((item) => (
                  <div
                    key={item.idNoSensible}
                    className="cursor-pointer border-b border-slate-100 p-2 hover:bg-slate-50"
                    onClick={() => handleSelectItem('noSensibles', item)}
                  >
                    <p className="font-medium">{item.sector}</p>
                    <p className="text-sm text-slate-600">{item.actividad}</p>
                    <p className="text-xs text-slate-500">MARGEN: {item.margen_maximo}</p>
                  </div>
                ))
              ) : searchText.noSensibles ? (
                <div className="p-2 text-center text-slate-500">NO SE ENCONTRARON RESULTADOS</div>
              ) : (
                <div className="p-2 text-center text-slate-500">ESCRIBA PARA BUSCAR</div>
              )}
            </div>
          )}

          {selectedActivities.noSensibles &&
            renderSelectedActivity(selectedActivities.noSensibles, 'noSensibles')}
        </div>
      </div>
    </div>
  );
};

export default ActividadesForm;
